import * as immutable from "immutable";
import {Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import {cloneDeep, find, isEmpty} from 'lodash';
import {map, mergeMap, pluck, switchMap, take,distinctUntilKeyChanged} from 'rxjs/operators';
import {of} from 'rxjs/observable/of';

import {DynamicSearchPayload, Filter} from '@ispm-shared/modules/common/dynamic-search';
import {DynamicSearchService} from './dynamic-search.service';
import {IPatient} from "@ispm/user/interfaces/patient.interface";
import {IgenomicsState} from '@ispm/core/interfaces/genomics-state.interface';
import {PatientService} from '@ispm-client/modules/patient/services/patient.service';
import {ResourceTypes} from '../interfaces';
import {SimpleSearchOrigin} from '../interfaces/simple-search.interface';
import {SimpleSearchService} from './simple-search.service';
import {TablelistHandlerService} from './tablelist-handler.service';
import {UserActionCreatorService} from '@ispm/user/services/user-action-creator.service';
import {diagnosticOrderAllItemsSelector} from '@ispm/dashboard/test/store/diagnostic-order.selector';
import {ArrayUtils} from "@ispm/shared/utils/Array.utils";

@Injectable()
export class TablelistService {
  constructor(
    private _dynamicSearchService: DynamicSearchService,
    private _handlerService: TablelistHandlerService,
    private _simpleSearchService: SimpleSearchService,
    private _store: Store<IgenomicsState>,
    private _userAC: UserActionCreatorService,
    private patientService: PatientService
  ) {
  }

  public loadConfigTemplate(resourceType: ResourceTypes, configTemplateType: string) {
    return this._handlerService.resourceHandler(resourceType).getConfigResourceTemplate(configTemplateType);
  }

  public getTotalEntities(
    resourceType: ResourceTypes, configTemplateType: string, dynamicSearchQuery?: DynamicSearchPayload,
    resource?: string, association?: Array<any>
  ) {
    this._handlerService.resourceHandler(resourceType, dynamicSearchQuery).dynamicSearchQuery = dynamicSearchQuery;
    this._handlerService.resourceHandler(resourceType, dynamicSearchQuery).type = configTemplateType;
    this._handlerService.resourceHandler(resourceType, dynamicSearchQuery).resource = resource;
    this._handlerService.resourceHandler(resourceType, dynamicSearchQuery).association = association;

    return this._handlerService.resourceHandler(resourceType, dynamicSearchQuery).getTotalEntities();
  }

  public getIds(
    resourceType: ResourceTypes, configTemplateType: string, query, page = 1, sortField, sortOrder, chunkSize = 400,
    hasSearchMode, dynamicSearchQuery?: DynamicSearchPayload, sortByType?: string, resource?: string, sortPath: boolean = true,
    associationItems?: Array<any>, adaptableFilters?: any, isClubbed: boolean = false, patientIds?: string
  ) {
    this._handlerService.resourceHandler(resourceType, dynamicSearchQuery).chunk = chunkSize;
    this._handlerService.resourceHandler(resourceType, dynamicSearchQuery).dynamicSearchQuery = dynamicSearchQuery;
    this._handlerService.resourceHandler(resourceType, dynamicSearchQuery).resource = resource;
    this._handlerService.resourceHandler(resourceType, dynamicSearchQuery).type = configTemplateType;

    return this._handlerService.resourceHandler(resourceType, dynamicSearchQuery)
      .getResourceIds(
        query,
        page,
        sortField,
        sortOrder,
        hasSearchMode,
        isClubbed,
        configTemplateType,
        resourceType,
        patientIds,
        sortByType,
        sortPath,
        associationItems,
        adaptableFilters,
      );
  }

  public getQuery(
    resourceType: ResourceTypes, configTemplateType: string, page = 1, sortField, sortOrder, chunkSize = 400,
    dynamicSearchQuery?: DynamicSearchPayload, sortByType?: string, resource?: string, sortPath: boolean = true,
    associationItems?: Array<any>, adaptableFilters?: any, patientIds?: string
  ) {
    this._handlerService.resourceHandler(resourceType, dynamicSearchQuery).chunk = chunkSize;
    this._handlerService.resourceHandler(resourceType, dynamicSearchQuery).type = configTemplateType;
    this._handlerService.resourceHandler(resourceType, dynamicSearchQuery).dynamicSearchQuery = dynamicSearchQuery;
    this._handlerService.resourceHandler(resourceType, dynamicSearchQuery).resource = resource;

    return this._handlerService.resourceHandler(resourceType, dynamicSearchQuery)
      .getQuery( page, sortField, sortOrder, sortByType, sortPath, associationItems, adaptableFilters,  patientIds);
  }

  public getData(
    resourceType: ResourceTypes, configTemplateType: string, field_list: string, resources_ids: Array<string>,
    dynamicSearchQuery?: DynamicSearchPayload, resource?: string, associationItems?: Array<any>,
    adaptableFilters?: any, fetchAdditionalData: boolean = false, additionalResourcesToRetrieve: any[] = []
  ) {
    this._handlerService.resourceHandler(resourceType, dynamicSearchQuery).type = configTemplateType;
    this._handlerService.resourceHandler(resourceType, dynamicSearchQuery).dynamicSearchQuery = dynamicSearchQuery;
    this._handlerService.resourceHandler(resourceType, dynamicSearchQuery).resource = resource;

    if (!fetchAdditionalData && !configTemplateType.startsWith('curation')) {
      return this._handlerService
        .resourceHandler(resourceType, dynamicSearchQuery)
        .getResource(field_list, resources_ids, associationItems, adaptableFilters);
    } else {
      const additionalParams = { };

      if (configTemplateType.startsWith('curation')) {
        additionalParams['type'] = configTemplateType;

        if (additionalResourcesToRetrieve.length > 0) {
          additionalParams['additionalResourcesToRetrieve'] = additionalResourcesToRetrieve;
        }
      } else {
        this._userAC.getCurrentUser()
          .pipe(
            take(1),
            pluck('id')
          )
          .subscribe(userId => {
            if (userId) {
              additionalParams['currentUserId'] = userId;
            }
          });
      }

      return this._handlerService
        .resourceHandler(resourceType, dynamicSearchQuery)
        .getResource(field_list, resources_ids, associationItems, adaptableFilters, additionalParams);
    }
  }

  public getResourceIdsPatientTests(resourceType: ResourceTypes, configTemplateType: string, patient_id, org_id) {
    this._handlerService.resourceHandler(resourceType).type = configTemplateType;

    return this._handlerService.resourceHandler(resourceType).getResourceIdsPatientTests(patient_id, org_id);
  }

  public getTreatments(resourceType: ResourceTypes, configTemplateType: string, patient_id, revision) {
    this._handlerService.resourceHandler(resourceType).type = configTemplateType;

    return this._handlerService.resourceHandler(resourceType).getTreatments(patient_id, revision);
  }

  public getSelectedTreatment(resourceType: ResourceTypes, configTemplateType: string, revision) {
    this._handlerService.resourceHandler(resourceType).type = configTemplateType;

    return this._handlerService.resourceHandler(resourceType).getSelectedTreatment(revision);
  }

  public getOrdersForTestAllItems(worklistConfig) {
    if (this._simpleSearchService.isSimpleSearch(worklistConfig)) {
      return this.getOrdersForTestAllItemsSimpleSearch(worklistConfig.simpleSearch);
    }

    return this.getOrdersForTestAllItemsDynamicSearch(
      worklistConfig.dynamicSearch?.dataQuery,
      worklistConfig.resource,
      worklistConfig.associationItems
    );
  }

  private getOrdersForTestAllItemsSimpleSearch(simpleSearchConfig) {
    const config = cloneDeep(simpleSearchConfig);

    return this._store.select('tests', 'diagnosticOrders', 'order')
      .pipe(
        take(1),
        mergeMap(order => {
          const patientId = order.subject;

          if (!patientId) {
            return of([]);
          }

          delete config.active; // All items should show archive tests thus removing archived filter

          // Todo: check if previous test revisions are needed and shown
          return this._simpleSearchService.search(config, SimpleSearchOrigin.ALL_ITEMS, null, 0, config.defaultSortField, -1, null, false, patientId).pipe(
            take(1),
            map(results => results)
          );
        })
      );
  }

  private getOrdersForTestAllItemsDynamicSearch(dataQuery, resourceType, associationItems) {
    dataQuery.target = {
      _page: 0,
      _count: 'ALL'
    };

    return this._store.select('users', 'patients')
      .pipe(
        ArrayUtils.filterEmpty(),
        take(1),
        mergeMap((data: immutable.Map<string, IPatient>) => {
          let patientObj: IPatient = null;

          // @ts-ignore
          for (let value of data.values()) {
            patientObj = value;
          }

          if (patientObj === null || patientObj === undefined) {
            return of([]);
          }

          let patientId = this.patientService.getPatientIDStrWithPrimary(patientObj)
          const patientAssociation = find(associationItems, associationItem => associationItem.name === 'patientId');

          if (patientAssociation) {
              const patientFilter = new Filter(
                patientAssociation.resource,
                patientAssociation.operator,
                patientId,
                patientAssociation.path,
                patientAssociation.type
              );

              // All items should show archive tests thus removing archived filter from the filters list (that are taken from the worklist)
              // Using includes() because the filter path can be in curly braces.
              dataQuery.filters = dataQuery.filters.filter((filter) =>
                !filter.path.toLowerCase().includes('activeworkflow')
                && !filter.path.toLowerCase().includes('wlrdoactivewfl')
              );

              dataQuery.filters.push(patientFilter);

              // GEN-105081
              let isLatestRevisionIdx = -1;

              dataQuery.filters.map((filter, idx) => {
                if (filter.path === '{isLatestRevision}' && filter.resource === 'UIPractWorkListRow') {
                  isLatestRevisionIdx = idx;
                }

                return filter;
              });

              if (isLatestRevisionIdx !== -1) {
                dataQuery.filters.splice(isLatestRevisionIdx, 1);
              }
            }
            // Data is already there in the store. If data is not available, dynamic search will be called.
            return this._store.select(diagnosticOrderAllItemsSelector)
              .pipe(
                take(1),
                switchMap((data: any) => {
                  if (!!data && isEmpty(data)) {
                    return this._dynamicSearchService.sendPureRequest(dataQuery, resourceType, 'clubbed')
                      .pipe(
                        map((resp: any) => resp.data)
                      );
                  }
                  return [data];
                })
              );
          })
      );
  }
}
